import { useCallback, useEffect, useState } from 'react';
import { playerConfigDomain } from '../../../util/config';
import axios from 'axios';
import { isUsedOnMobile, setAdditionalStyleSheet } from '../../../util/util';
import i18n from '../../../i18n';
import { useDispatch } from 'react-redux';
import { setChatEmail, setChatUserId, setChatUsername } from '../../../actions/chatLoginActions';
import Cookies from 'js-cookie';

interface UseFetchPlayerConfigDataProps {
	playerConfigId: string;
	accountId: string;
	fromSceneEditor?: boolean;
	queryParams?: any;
	isMediaLive?: boolean;
	setLoading?: (_value: boolean) => void;
}

const SCENE_EDITOR_PLAYER_CONFIG_ID = '847a7a05-52c8-4495-bf26-5cae9eea2f86';
const SCENE_EDITOR_PLAYER_ACCOUNT_ID = 'Accpy7KNHj1IUilFL5BMuNL0Q';

const useFetchPlayerConfigData = ({ playerConfigId, accountId, fromSceneEditor, queryParams, isMediaLive, setLoading }: UseFetchPlayerConfigDataProps) => {
	const dispatch = useDispatch();
	const [playerMobileConfig, setPlayerMobileConfig] = useState<any>({});
	const [playerDesktopConfig, setPlayerDesktopConfig] = useState<any>({});

	const [playerConfig, setPlayerConfig] = useState<any>();

	const isIgnoreChatCookies = useCallback((settings: any, ignoreChatCookies?: any) => {
		if (ignoreChatCookies) {
			return ignoreChatCookies === 'true';
		}
		return settings?.additionalSettings?.noChatCookies === true;
	}, []);

	const fetchPlayerConfigCallback = useCallback((desktopConfig?: any, mobileConfig?: any) => {
		if (fromSceneEditor) {
			return;
		}

		const settings = isUsedOnMobile(queryParams ?? {}) ? mobileConfig : desktopConfig;
		if (Object.keys(settings).length === 0) {
			return;
		}

		const language = queryParams.language ? queryParams.language : settings?.accessibilitySettings?.language;
		if (language) {
			i18n.changeLanguage(language);
			document.documentElement.lang = language || 'en';
		}

		if (isIgnoreChatCookies(settings, queryParams.ignoreChatCookies)) {
			Cookies.remove(`chat_ID_${accountId}`);
			Cookies.remove(`chat_name_${accountId}`);
			Cookies.remove(`chat_email_${accountId}`);
			dispatch(setChatUserId(''));
			dispatch(setChatUsername(''));
			dispatch(setChatEmail(''));
		}

		setAdditionalStyleSheet({
			settings,
			isMediaLive,
			paramSharing: queryParams.sharing,
			paramDownload: queryParams.downloadMedia,
		});
	}, [fromSceneEditor, queryParams, isIgnoreChatCookies, isMediaLive, accountId, dispatch]);

	const setPlayerConfigData = useCallback((configData: any) => {
		if (configData && configData.layouts) {
			const desktop = configData.layouts.find((el: any) => {
				return el.type === 'desktop';
			});
			desktop && setPlayerDesktopConfig(desktop);

			const mobile = configData.layouts.find((el: any) => {
				return el.type === 'mobile';
			});
			mobile && setPlayerMobileConfig(mobile);

			setPlayerConfig(configData);
			setLoading?.(false);
		}
	}, [setLoading]);

	useEffect(() => {
		let configUrl = accountId ? `${playerConfigDomain}${accountId}/settings/${playerConfigId || "Enterprise"}`: "";
		const enterpriseConfigUrl = `${playerConfigDomain}${accountId ?? SCENE_EDITOR_PLAYER_ACCOUNT_ID}/settings/Enterprise`;
		if (fromSceneEditor) {
			configUrl = `${playerConfigDomain}${SCENE_EDITOR_PLAYER_ACCOUNT_ID}/settings/${SCENE_EDITOR_PLAYER_CONFIG_ID}`;
		}

		axios.get(configUrl).then((response) => {
			if (response?.data) {
				setPlayerConfigData(response?.data);
			} else {
				axios.get(enterpriseConfigUrl).then((response) => {
					response?.data && setPlayerConfigData(response?.data);
				});
			}
		}).catch((_error) => {
			axios.get(enterpriseConfigUrl).then((response) => {
				response?.data && setPlayerConfigData(response?.data);
			});
		});
	}, [playerConfigId, accountId, fromSceneEditor, setPlayerConfigData]);

	useEffect(() => {
		fetchPlayerConfigCallback?.(playerDesktopConfig, playerMobileConfig);
	}, [playerMobileConfig, playerDesktopConfig, fetchPlayerConfigCallback]);

	return { playerConfig, playerMobileConfig, playerDesktopConfig };
};

export default useFetchPlayerConfigData;
